import React, { useEffect, useRef, useState } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const Richtexteditor = ({
  value,
  onChange,
  style,
  placeholder,
  readOnly = false,
}: {
  value: string;
  onChange?: (content: string) => void;
  style?: React.CSSProperties;
  placeholder?: string;
  readOnly?: boolean;
}) => {
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);
  const [scale, setScale] = useState(1);
  const imgRef = useRef<HTMLImageElement | null>(null);

  useEffect(() => {
    const handleImageClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.tagName === 'IMG') {
        setZoomSrc((target as HTMLImageElement).src);
        setScale(1);
      }
    };

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setZoomSrc(null);
      }
    };

    document.addEventListener('click', handleImageClick);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('click', handleImageClick);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, []);

  // Pinch-to-zoom support
  useEffect(() => {
    const image = imgRef.current;
    if (!image) return;

    let startDistance = 0;
    let startScale = scale;

    const getDistance = (touches: TouchList) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        startDistance = getDistance(e.touches);
        startScale = scale;
      }
    };

    const onTouchMove = (e: TouchEvent) => {
      if (e.touches.length === 2) {
        const distance = getDistance(e.touches);
        const newScale = startScale * (distance / startDistance);
        setScale(Math.max(1, Math.min(newScale, 5)));
      }
    };

    image.addEventListener('touchstart', onTouchStart);
    image.addEventListener('touchmove', onTouchMove);

    return () => {
      image.removeEventListener('touchstart', onTouchStart);
      image.removeEventListener('touchmove', onTouchMove);
    };
  }, [scale, zoomSrc]);

  return (
    <>
      <ReactQuill
        theme="snow"
        value={value}
        onChange={onChange}
        style={style}
        placeholder={placeholder}
        readOnly={readOnly}
        modules={{
          toolbar: readOnly
            ? false
            : [
                [{ header: [1, 2, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ list: 'ordered' }, { list: 'bullet' }],
                ['link', 'image'],
                ['clean'],
              ],
        }}
      />

      {zoomSrc && (
        <div className="image-zoom-overlay" onClick={() => setZoomSrc(null)}>
          <button
            className="zoom-close-btn"
            onClick={(e) => {
              e.stopPropagation();
              setZoomSrc(null);
            }}
            aria-label="Close image"
          >
            ❌
          </button>
          <img
            ref={imgRef}
            src={zoomSrc}
            alt="Zoomed"
            style={{ transform: `scale(${scale})` }}
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </>
  );
};

export default Richtexteditor;
